import { useState } from "react";

import { SearchBox } from "@mapbox/search-js-react";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { addressIsEmpty } from "components/Shipper/components/CreateShipmentForm/components/RoutePoint/utils";
import { toFullReadableAddress } from "utils";

import type {
  SearchBoxFeatureSuggestion,
  SearchBoxRetrieveResponse,
} from "@mapbox/search-js-core";

import type { MapBoxLocationType } from "./types";

// TOUCH PAD PROBLEM https://stackoverflow.com/questions/76382143/mapbox-searchbox-do-not-support-mouse-click-when-selecting-a-suggestion

const TOKEN = import.meta.env.REACT_APP_MAPBOX_PUBLIC_KEY ?? "ERROR";

interface Props {
  onSuggestionSelected: (suggestion: SearchBoxFeatureSuggestion) => void;
  onClear: () => void;
  locationTypes?: Set<MapBoxLocationType>; // limits search results to the given location types.
  // https://docs.mapbox.com/mapbox-search-js/api/core/search/#searchboxadministrativeunittype
  updateInputModeForRoutePoint: (mode: "autocomplete" | "manual") => void;
  routePoint: EditableRoutePointState;
}

// TODO: refactor to handle intense prop drilling
export function AddressAutocompleteContainerV2(props: Props) {
  const {
    onSuggestionSelected,
    onClear,
    locationTypes,
    updateInputModeForRoutePoint,
    routePoint,
  } = props;

  const [queryError, setQueryError] = useState<UIError>(null);

  const fullAddress = toFullReadableAddress(routePoint.point_location);

  const onRetrieve = (res: SearchBoxRetrieveResponse) => {
    // suggestion is a collection of GeoJSON Feature
    // https://docs.mapbox.com/mapbox-search-js/api/core/search/#searchboxfeaturesuggestion
    if (res.features.length > 0) {
      const feature = res.features[0];
      onSuggestionSelected(feature);
    }
  };

  const onSuggestError = () => {
    setQueryError("Failed to retrieve suggestions");
  };

  // if address is not empty and business name is blank,
  // then update manual prompt to include 'add business name prompt'
  const showAddBusinessNameLink =
    !addressIsEmpty(routePoint) &&
    routePoint.point_location?.business_name === "";

  return (
    <div className="mapbox-searchbox-wrapper" style={{ width: "100%" }}>
      <Stack direction="column" width="100%">
        {/* // https://docs.mapbox.com/mapbox-search-js/api/react/search/ */}
        <Box position="relative">
          <SearchBox
            accessToken={TOKEN}
            onRetrieve={onRetrieve}
            onSuggestError={onSuggestError}
            value={fullAddress}
            placeholder="XYZ Distributors, 456 Main Street, Toronto, ON, M5V 1J1"
            options={{
              language: "en",
              types: locationTypes,
              limit: 10,
            }}
            theme={{
              variables: {
                boxShadow: "0 0 0 1px silver",
              },
              cssText:
                ".ClearBtn { display: none !important; } .LoadingIcon { display: none !important; }",
            }}
          />
          {fullAddress.length > 0 && (
            <IconButton
              onClick={onClear}
              size="small"
              aria-label="Clear search"
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
                padding: "2px",
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {showAddBusinessNameLink
            ? "Business name not found."
            : "Not finding the address or need to make an edit?"}{" "}
          <Button
            onClick={() => updateInputModeForRoutePoint("manual")}
            variant="text"
            color="primary"
            sx={{
              textTransform: "none",
              padding: 0,
              minWidth: "unset",
              fontSize: "inherit",
              lineHeight: "inherit",
              verticalAlign: "baseline",
            }}
          >
            {showAddBusinessNameLink
              ? "Add manually"
              : "Switch to manual entry."}
          </Button>
        </Typography>
        <ErrorTypography error={queryError} />
      </Stack>
    </div>
  );
}
